/**
 * ⌘K / Ctrl+K from anywhere: find a person by name, any part of a phone number or an email, or a
 * policy by its number, and jump to them. Also the quickest way to the other pages. Arrow keys
 * move, Enter opens, Escape closes.
 */
import { formatPhone, lifecycleLabel, policyStageLabel, type Permission } from "@tlcrm/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";

import { useSearch } from "../api/hooks";
import { useMe } from "../auth/session";
import { initials } from "../lib/format";
import { Icon } from "./Icon";
import type { IconName } from "./icons";

interface Item {
  key: string;
  group: string;
  icon?: IconName;
  avatar?: string;
  label: string;
  meta: string[];
  to: string;
}

const page = (key: string, icon: IconName, label: string, to: string): Item => ({
  key: `p-${key}`,
  group: "Go to",
  icon,
  label,
  meta: [],
  to,
});

/** The navigation, in its order; pages a role cannot open are left out. */
function pagesFor(can: (p: Permission) => boolean): Item[] {
  return [
    page("home", "home", "Home", "/"),
    page("leads", "inbox", "Leads", "/leads"),
    page("contacts", "users", "Customers", "/contacts"),
    page("policies", "briefcase", "Policies", "/policies"),
    page("book", "dollar", "Book of Business", "/policies?view=book"),
    page("calendar", "calendar", "Calendar", "/calendar"),
    page("dashboards", "chart", "Dashboards", "/dashboards"),
    page("reports", "list", "Reports", "/reports"),
    ...(can("commission:read")
      ? [page("commissions", "dollar", "Commissions", "/commissions")]
      : []),
    page("tasks", "checkSquare", "Tasks", "/tasks"),
    page("calls", "phoneIn", "Unmatched calls", "/calls"),
    ...(can("admin:lists") ? [page("admin", "shield", "Admin", "/admin")] : []),
  ];
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CommandBar({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { can } = useMe();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const query = useDebounced(q.trim(), 150);
  const search = useSearch(query);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  const items = useMemo<Item[]>(() => {
    const term = q.trim().toLowerCase();
    const pages = pagesFor(can).filter((p) => !term || p.label.toLowerCase().includes(term));
    if (query.length < 2 || !search.data) return pages;
    const contacts: Item[] = search.data.contacts.map((c) => ({
      key: c.id,
      group: "People",
      avatar: initials(c.fullName),
      label: c.fullName,
      meta: [
        c.primaryPhone ? formatPhone(c.primaryPhone) : "",
        lifecycleLabel(c.lifecycleStage),
        c.state ?? "",
      ].filter(Boolean),
      to: `/contacts/${c.id}`,
    }));
    const policies: Item[] = search.data.policies.map((p) => ({
      key: p.id,
      group: "Policies",
      icon: "briefcase",
      label: p.name,
      meta: [p.carrier ?? "", p.policyNumber ?? "", policyStageLabel(p.stage)].filter(Boolean),
      to: `/policies/${p.id}`,
    }));
    return [...contacts, ...policies, ...pages];
  }, [q, query, search.data, can]);

  // Results arrive after the keystroke that asked for them; keep the highlight on a real row.
  const current = Math.min(active, Math.max(0, items.length - 1));

  const open = (item: Item | undefined) => {
    if (!item) return;
    onClose();
    navigate(item.to);
  };

  return createPortal(
    <>
      <div className="modal-scrim" onClick={onClose} />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Search">
        <div className="palette-input">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            placeholder="Name, phone, email or policy number"
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive(Math.min(items.length - 1, current + 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive(Math.max(0, current - 1));
              }
              if (e.key === "Enter") open(items[current]);
            }}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={items[current] ? `pi-${items[current].key}` : undefined}
          />
          {search.isFetching && (
            <span className="t3" style={{ fontSize: "var(--fs-xs)" }}>
              Searching…
            </span>
          )}
        </div>
        <div className="palette-list" id="palette-list" role="listbox">
          {query.length >= 2 &&
            search.data &&
            search.data.contacts.length + search.data.policies.length === 0 && (
              <div className="palette-empty">Nobody matches “{query}”.</div>
            )}
          {items.map((item, i) => (
            <div key={item.key}>
              {(i === 0 || items[i - 1]!.group !== item.group) && (
                <div className="palette-group eyebrow">{item.group}</div>
              )}
              <button
                id={`pi-${item.key}`}
                role="option"
                aria-selected={i === current}
                className={`palette-item${i === current ? " is-active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => open(item)}
              >
                {item.avatar ? (
                  <span className="avatar">{item.avatar}</span>
                ) : (
                  item.icon && <Icon name={item.icon} />
                )}
                <span>{item.label}</span>
                <span className="meta">
                  {item.meta.map((m) => (
                    <span key={m} className={/\d{3}\)/.test(m) ? "mono" : undefined}>
                      {m}
                    </span>
                  ))}
                </span>
              </button>
            </div>
          ))}
        </div>
        <div className="palette-foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}
